import { useQuery } from '@tanstack/react-query';
import { Shield, FileCheck, AlertTriangle, Users } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

export function CompliancePage() {
  const modelCards = useQuery({
    queryKey: ['ai-gov', 'model-cards'],
    queryFn: async () => (await api.get('/api/ai-governance/model-cards')).data,
  });

  const risks = useQuery({
    queryKey: ['ai-gov', 'risks'],
    queryFn: async () => (await api.get('/api/ai-governance/risks')).data,
  });

  const dsrs = useQuery({
    queryKey: ['gdpr', 'requests'],
    queryFn: async () => (await api.get('/api/gdpr/requests')).data,
  });

  const ropa = useQuery({
    queryKey: ['gdpr', 'ropa'],
    queryFn: async () => (await api.get('/api/gdpr/ropa')).data,
  });

  const iou = useQuery({
    queryKey: ['ai-gov', 'iou'],
    queryFn: async () => (await api.get('/api/ai-governance/instructions-for-use')).data,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Compliance</h1>
        <p className="text-sm text-muted-foreground">
          EU AI Act · GDPR · ISO 27001 · 21 CFR Part 11 — operational artefacts.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          icon={FileCheck}
          label="Model cards"
          value={modelCards.data?.items?.length ?? '—'}
        />
        <StatCard
          icon={AlertTriangle}
          label="Open risks"
          value={
            risks.data?.items?.filter(
              (r: { status: string }) => r.status === 'OPEN',
            ).length ?? '—'
          }
        />
        <StatCard
          icon={Users}
          label="DSRs in progress"
          value={
            dsrs.data?.items?.filter(
              (r: { status: string }) =>
                r.status !== 'COMPLETED' && r.status !== 'REJECTED',
            ).length ?? '—'
          }
        />
        <StatCard
          icon={Shield}
          label="ROPA activities"
          value={ropa.data?.items?.length ?? '—'}
        />
      </div>

      {/* Model cards */}
      <Card>
        <CardHeader>
          <CardTitle>EU AI Act — Model cards</CardTitle>
          <CardDescription>
            Article 11 technical documentation for each deployed model.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Model</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Risk</TableHead>
                <TableHead>Approved</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {modelCards.data?.items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    No model cards registered yet. Create one via the API.
                  </TableCell>
                </TableRow>
              )}
              {modelCards.data?.items?.map((c: {
                id: string;
                modelName: string;
                provider: string;
                version: string;
                riskLevel: string;
                approvedAt: string | null;
              }) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.modelName}</TableCell>
                  <TableCell>{c.provider}</TableCell>
                  <TableCell>{c.version}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        c.riskLevel === 'UNACCEPTABLE'
                          ? 'destructive'
                          : c.riskLevel === 'HIGH'
                            ? 'warning'
                            : 'secondary'
                      }
                    >
                      {c.riskLevel}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {c.approvedAt ? (
                      <Badge variant="success">{formatDate(c.approvedAt)}</Badge>
                    ) : (
                      <Badge variant="warning">Pending</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Risk register */}
      <Card>
        <CardHeader>
          <CardTitle>Risk register</CardTitle>
          <CardDescription>
            EU AI Act Art. 9 risk management — identified risks and mitigations.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Area</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Residual</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {risks.data?.items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-muted-foreground">
                    No risks registered yet.
                  </TableCell>
                </TableRow>
              )}
              {risks.data?.items?.map((r: {
                id: string;
                area: string;
                description: string;
                residualRisk: string;
                status: string;
              }) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.area}</TableCell>
                  <TableCell className="max-w-md text-xs">{r.description}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        r.residualRisk === 'VERY_HIGH' || r.residualRisk === 'HIGH'
                          ? 'destructive'
                          : r.residualRisk === 'MEDIUM'
                            ? 'warning'
                            : 'success'
                      }
                    >
                      {r.residualRisk}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{r.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Data subject requests */}
      <Card>
        <CardHeader>
          <CardTitle>GDPR — Data subject requests</CardTitle>
          <CardDescription>
            Article 12-22 response tracking. 30-day statutory deadline.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subject</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Received</TableHead>
                <TableHead>Due by</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dsrs.data?.items?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                    No open data subject requests.
                  </TableCell>
                </TableRow>
              )}
              {dsrs.data?.items?.map((r: {
                id: string;
                subjectEmail: string;
                type: string;
                status: string;
                receivedAt: string;
                dueBy: string;
              }) => (
                <TableRow key={r.id}>
                  <TableCell>{r.subjectEmail}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{r.type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        r.status === 'COMPLETED'
                          ? 'success'
                          : r.status === 'REJECTED'
                            ? 'destructive'
                            : 'warning'
                      }
                    >
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">{formatDate(r.receivedAt)}</TableCell>
                  <TableCell className="text-xs">{formatDate(r.dueBy)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Instructions for Use */}
      {iou.data && (
        <Card>
          <CardHeader>
            <CardTitle>Instructions for Use</CardTitle>
            <CardDescription>
              EU AI Act Article 13 — human-readable disclosure.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="scrollbar-thin max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">
              {JSON.stringify(iou.data, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}
